import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button, Form, Table, Spinner, Row, Col } from 'react-bootstrap';
import Layout from '../components/Layout';
import appConstants from '../constants/appConstants';
import { FaArrowLeft, FaSave } from 'react-icons/fa';

const getAuthToken = () => localStorage.getItem('authToken');

const MarkEntryPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const termExamId = searchParams.get('term_exam_id');
  const subjectId = searchParams.get('subject_id');

  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [marks, setMarks] = useState({});
  const [examInfo, setExamInfo] = useState({});
  const [maxMark, setMaxMark] = useState('');
  const [originalMaxMark, setOriginalMaxMark] = useState('');
  const [isMaxMarkChanged, setIsMaxMarkChanged] = useState(false);

  const fetchStudents = async () => {
    setLoading(true);
    const token = getAuthToken();
    try {
      const res = await axios.get(
        `${appConstants.API_ROOT_URL}get-term-exam-students-v2?term_exam_id=${termExamId}&subject_id=${subjectId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data.status === 'success') {
        const studentData = res.data.data;
        setStudents(studentData);
        const examInfo = res.data.exam_info;
        setExamInfo(examInfo);
        const maxMarkValue = examInfo.max_mark || '';
        setMaxMark(maxMarkValue);
        setOriginalMaxMark(maxMarkValue);
        setIsMaxMarkChanged(false);
        const initialMarks = {};
        studentData.forEach((s) => {
          initialMarks[s.id] = s.marks_obtained || '';
        });
        setMarks(initialMarks);
      } else {
        alert(res.data.message || 'Failed to load students');
      }
    } catch (err) {
      alert('Error fetching students');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkChange = (studentId, value) => {
    setMarks((prev) => ({
      ...prev,
      [studentId]: value,
    }));
  };

  const handleMaxMarkChange = (value) => {
    setMaxMark(value);
    setIsMaxMarkChanged(value !== originalMaxMark);
  };

  const saveMark = async (studentId, value) => {
    const token = getAuthToken();
    try {
      await axios.post(
        `${appConstants.API_ROOT_URL}save-exam-mark-v2`,
        {
          term_exam_id: termExamId,
          subject_id: subjectId,
          student_id: studentId,
          marks_obtained: value,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch (err) {
      console.error(`Error saving mark for student ${studentId}:`, err);
    }
  };

  const handleBlur = (studentId) => {
    saveMark(studentId, marks[studentId]);
  };

  const handleKeyDown = (e, studentId) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveMark(studentId, marks[studentId]);
    }
  };

  useEffect(() => {
    if (termExamId && subjectId) {
      fetchStudents();
    }
  }, [termExamId, subjectId]);

  const handleBackClick = () => {
    navigate(-1);
  };

  return (
    <Layout>
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <>
          {/* Compact Top Bar */}
          <div
            style={{
              background: '#343a40',
              color: '#fff',
              borderRadius: 8,
              padding: '0.6rem 1rem',
              marginBottom: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
              <div style={{ fontSize: 18, fontWeight: 600 }}>
                Enter{' '}
                {examInfo.evaluation_type === 'grade' ? 'Grades' : 'Marks'}
              </div>
              <div style={{ fontSize: 13, opacity: 0.9 }}>
                {examInfo.exam_name} - {examInfo.subject_name} -{' '}
                {examInfo.class_name} {examInfo.division}
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
              {/* Maximum Mark Section - Only for Mark-based exams */}
              {examInfo.evaluation_type === 'mark' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>
                    Max Mark:
                  </span>
                  <input
                    type="number"
                    value={maxMark}
                    onChange={(e) => handleMaxMarkChange(e.target.value)}
                    style={{
                      width: 80,
                      height: 32,
                      padding: '4px 8px',
                      borderRadius: 4,
                      border: '1px solid rgba(255,255,255,0.3)',
                      backgroundColor: 'rgba(255,255,255,0.15)',
                      color: '#fff',
                      fontSize: 13,
                      textAlign: 'center',
                      boxSizing: 'border-box',
                    }}
                    placeholder="Max"
                  />
                  {isMaxMarkChanged && (
                    <Button
                      size="sm"
                      onClick={async () => {
                        const token = getAuthToken();
                        try {
                          await axios.post(
                            `${appConstants.API_ROOT_URL}save_exam_max_mark`,
                            {
                              term_exam_id: termExamId,
                              subject_id: subjectId,
                              max_mark: maxMark,
                            },
                            {
                              headers: { Authorization: `Bearer ${token}` },
                            },
                          );
                          setOriginalMaxMark(maxMark);
                          setIsMaxMarkChanged(false);
                          alert('Maximum mark saved.');
                        } catch (err) {
                          console.error('Error saving max mark:', err);
                          alert('Failed to save maximum mark.');
                        }
                      }}
                      style={{
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        border: '1px solid rgba(255,255,255,0.3)',
                        color: '#fff',
                        padding: '6px 8px',
                        fontSize: 12,
                        borderRadius: 4,
                        width: 32,
                        height: 32,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      title="Save Maximum Mark"
                    >
                      <FaSave />
                    </Button>
                  )}
                </div>
              )}

              <Button
                variant="outline-light"
                size="sm"
                style={{
                  borderRadius: 6,
                  padding: '6px 10px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onClick={handleBackClick}
              >
                <FaArrowLeft />
              </Button>
            </div>
          </div>

          {/* Students Table */}
          <div
            style={{
              background: '#fff',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              overflow: 'hidden',
            }}
          >
            <Table hover className="mb-0">
              <thead
                style={{
                  background: '#f8f9fa',
                  borderBottom: '3px solid #dee2e6',
                }}
              >
                <tr>
                  <th
                    style={{
                      padding: '12px 20px',
                      fontWeight: '700',
                      color: '#212529',
                      fontSize: '0.95rem',
                      width: '80px',
                      letterSpacing: '0.5px',
                    }}
                  >
                    #
                  </th>
                  <th
                    style={{
                      padding: '12px 20px',
                      fontWeight: '700',
                      color: '#212529',
                      fontSize: '0.95rem',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Student Name
                  </th>
                  <th
                    style={{
                      padding: '12px 20px',
                      fontWeight: '700',
                      color: '#212529',
                      fontSize: '0.95rem',
                      width: '250px',
                      letterSpacing: '0.5px',
                    }}
                  >
                    {examInfo.evaluation_type === 'grade' ? 'Grade' : 'Marks'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {students.map((s, idx) => (
                  <tr key={s.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td
                      style={{
                        padding: '10px 20px',
                        verticalAlign: 'middle',
                        color: '#6c757d',
                        fontWeight: '600',
                      }}
                    >
                      {idx + 1}
                    </td>
                    <td
                      style={{
                        padding: '10px 20px',
                        verticalAlign: 'middle',
                        fontWeight: '500',
                        color: '#2c3e50',
                      }}
                    >
                      {s.student_name}
                    </td>
                    <td
                      style={{ padding: '10px 20px', verticalAlign: 'middle' }}
                    >
                      <Form.Control
                        type="text"
                        value={marks[s.id] || ''}
                        onChange={(e) => handleMarkChange(s.id, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, s.id)}
                        style={{
                          padding: '6px 10px',
                          borderRadius: '6px',
                          border: '2px solid #e9ecef',
                          fontSize: '0.9rem',
                          transition: 'all 0.3s ease',
                          fontWeight: '600',
                          height: '36px',
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#667eea';
                          e.target.style.boxShadow =
                            '0 0 0 3px rgba(102, 126, 234, 0.1)';
                          e.target.style.background = '#f8f9ff';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#e9ecef';
                          e.target.style.boxShadow = 'none';
                          e.target.style.background = '#fff';
                          handleBlur(s.id);
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {/* Help Text */}
          <div
            style={{
              marginTop: '20px',
              padding: '16px 20px',
              background: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e9ecef',
            }}
          >
            <p
              className="mb-0"
              style={{ fontSize: '0.9rem', color: '#6c757d' }}
            >
              <strong>Tip:</strong> Press <kbd>Enter</kbd> after entering a mark
              to automatically save and move to the next field.
              {examInfo.evaluation_type === 'grade' && (
                <> Enter grades like A+, A, B+, B, C, etc.</>
              )}
            </p>
          </div>
        </>
      )}
    </Layout>
  );
};

export default MarkEntryPage;
